import React, { useMemo, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { toast } from 'sonner'
import { Wallet, ArrowLeft, FileText, Printer, Plus, MessageSquare, MessageCircle, Mail, ScrollText, ShieldCheck, CalendarCheck, CalendarX } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'
import PaymentModal from '../components/PaymentModal'
import DatePopover from '../components/DatePopover'
import { cn } from '../lib/utils'

// Round to cents BEFORE formatting. The running balance is accumulated in floating point, so a
// settled account came out as -0.0000000001 and printed as "R-0,00" — which reads, on a statement,
// as if the practice owes the patient money.
const rand = (n) => {
  const v = Math.round(((Number(n) || 0) + Number.EPSILON) * 100) / 100
  return `R${(v === 0 ? 0 : v).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}
const dz = (iso) => (iso ? new Date(iso).toLocaleDateString('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' }) : '—')
const dtz = (iso) => (iso ? new Date(iso).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '')

function Stat({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-brand-border bg-white px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-brand-muted">{label}</p>
      <p className={`mt-1 text-xl font-semibold ${accent || 'text-brand-ink'}`}>{value}</p>
    </div>
  )
}

// Quick-glance risk badge — appointment reliability + a payer signal. Read-only; just flags.
function RiskBadge({ risk }) {
  if (!risk) return null
  const payer = {
    good:  { label: 'Payments OK', cls: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
    watch: { label: 'Watch (60+ days)', cls: 'border-amber-200 bg-amber-50 text-amber-700' },
    slow:  { label: 'Slow payer (90+ days)', cls: 'border-red-200 bg-red-50 text-red-700' },
  }[risk.payer] || { label: 'Payments OK', cls: 'border-brand-border bg-white text-brand-muted' }
  const rel = risk.reliability
  const relCls = rel == null ? 'text-brand-muted' : rel >= 90 ? 'text-emerald-700' : rel >= 75 ? 'text-amber-700' : 'text-red-700'
  return (
    <div className="rounded-xl border border-brand-border bg-white px-5 py-4">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-brand-muted">
        <ShieldCheck size={13} /> Risk at a glance
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm">
        <span className={`font-semibold ${relCls}`}>
          {rel == null ? 'No visit history' : `${rel}% reliability`}
        </span>
        <span className="inline-flex items-center gap-1 text-emerald-700"><CalendarCheck size={14} /> {risk.kept} kept</span>
        <span className="inline-flex items-center gap-1 text-red-600"><CalendarX size={14} /> {risk.no_show} missed</span>
        {risk.cancelled > 0 && <span className="text-brand-muted">{risk.cancelled} cancelled</span>}
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${payer.cls}`}>{payer.label}</span>
        {(risk.overdue || 0) > 0 && <span className="text-xs text-red-600">{rand(risk.overdue)} overdue 60+ days</span>}
      </div>
    </div>
  )
}

// Ageing of outstanding debt — current / 30 / 60 / 90+.
function AgeingBox({ ageing }) {
  if (!ageing) return null
  const buckets = [
    { k: 'current', label: 'Current', cls: 'text-brand-ink' },
    { k: 'd30', label: '30 days', cls: 'text-amber-600' },
    { k: 'd60', label: '60 days', cls: 'text-orange-600' },
    { k: 'd90', label: '90+ days', cls: 'text-red-600' },
  ]
  const total = buckets.reduce((s, b) => s + (Number(ageing[b.k]) || 0), 0)
  if (total <= 0) return null
  return (
    <div className="rounded-xl border border-brand-border bg-white px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-brand-muted">Ageing of debt</p>
      <div className="mt-2 grid grid-cols-4 gap-2 text-center">
        {buckets.map((b) => (
          <div key={b.k}>
            <p className="text-[11px] text-brand-muted">{b.label}</p>
            <p className={`text-sm font-semibold ${(Number(ageing[b.k]) || 0) > 0 ? b.cls : 'text-brand-muted/50'}`}>{rand(ageing[b.k])}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

// Credit available, tagged by source (deposit vs overpayment/other).
function CreditBox({ credit_breakdown }) {
  const cb = credit_breakdown || {}
  if ((Number(cb.total) || 0) <= 0) return null
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/50 px-5 py-4">
      <p className="text-xs font-medium uppercase tracking-wide text-indigo-700/80">Credit available</p>
      <p className="mt-1 text-xl font-semibold text-indigo-700">{rand(cb.total)}</p>
      <div className="mt-1.5 flex flex-wrap gap-2 text-xs">
        {(Number(cb.deposit) || 0) > 0 && (
          <span className="inline-flex items-center rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-indigo-700">Deposit {rand(cb.deposit)}</span>
        )}
        {(Number(cb.overpayment) || 0) > 0 && (
          <span className="inline-flex items-center rounded-full border border-brand-border bg-white px-2 py-0.5 text-brand-muted">Overpayment {rand(cb.overpayment)}</span>
        )}
      </div>
    </div>
  )
}

function TagChip({ tag }) {
  if (!tag) return null
  const cls = {
    Deposit:  'border-indigo-200 bg-indigo-50 text-indigo-700',
    Discount: 'border-purple-200 bg-purple-50 text-purple-700',
    Payment:  'border-emerald-200 bg-emerald-50 text-emerald-700',
  }[tag] || 'border-brand-border bg-white text-brand-muted'
  return <span className={`ml-2 inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{tag}</span>
}

function ConversationCard({ convo }) {
  const [open, setOpen] = useState(false)
  const last = convo.messages?.[convo.messages.length - 1]
  return (
    <div className="rounded-xl border border-brand-border bg-white">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-brand-surface/40">
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-sm font-medium text-brand-ink">
            {convo.patient_name}
            <span className="rounded-full bg-brand-surface px-2 py-0.5 text-[10px] uppercase tracking-wide text-brand-muted">{convo.channel}</span>
            {convo.topic && <span className="text-xs font-normal text-brand-muted">· {convo.topic}</span>}
          </p>
          {last && <p className="mt-0.5 truncate text-xs text-brand-muted">{last.role === 'assistant' ? 'Us: ' : ''}{last.content}</p>}
        </div>
        <div className="shrink-0 text-right">
          <p className="text-xs text-brand-muted">{dtz(convo.updated_at)}</p>
          <p className="text-[11px] text-brand-muted/70">{convo.message_count} msg{convo.message_count === 1 ? '' : 's'}</p>
        </div>
      </button>
      {open && (
        <div className="max-h-96 space-y-2 overflow-y-auto border-t border-brand-border bg-brand-surface/30 px-4 py-3">
          {(convo.messages || []).map((m, i) => {
            const us = m.role === 'assistant' || m.role === 'system'
            return (
              <div key={i} className={`flex ${us ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-3 py-1.5 text-sm ${us ? 'bg-brand-primary text-white' : 'bg-white text-brand-ink border border-brand-border'}`}>
                  <p className="whitespace-pre-wrap break-words">{m.content}</p>
                  {m.at && <p className={`mt-0.5 text-[10px] ${us ? 'text-white/70' : 'text-brand-muted'}`}>{dtz(m.at)}</p>}
                </div>
              </div>
            )
          })}
          {(convo.messages || []).length === 0 && <p className="text-center text-xs text-brand-muted">No messages recorded.</p>}
        </div>
      )}
    </div>
  )
}

export default function BillingAccountShow({
  account = {}, period = {}, balance = {}, opening_balance = 0, openings_by_patient = {},
  transactions = [], invoices = [], ageing = null, risk = null, credit_breakdown = null, conversations = [],
}) {
  const [from, setFrom] = useState(period.from || '')
  const [to, setTo]     = useState(period.to || '')
  const [payOpen, setPayOpen] = useState(false)
  const [tab, setTab]   = useState('ledger')      // 'ledger' | 'comms'
  const [scope, setScope] = useState('all')       // 'all' | patientId (as string)

  const applyRange = () => router.get(`/accounts/${account.id}`, { from, to }, { preserveScroll: true, preserveState: true })
  // The statement PDF follows the SAME scope as the on-screen ledger: whole account, or the one
  // selected member. Paul 2026-08-04 — picking a member used to filter only the screen, so the
  // printed/downloaded PDF still listed every member on the account.
  // The one description of "which statement" — used by the PDF link, Print, WhatsApp and email
  // alike, so all four can never drift apart again.
  const statementScope = () => (scope === 'all' ? { from, to } : { from, to, patient_id: scope })
  const statementUrl = `/accounts/${account.id}/statement.pdf?from=${from}&to=${to}${scope === 'all' ? '' : `&patient_id=${scope}`}`

  // Print = print the server-rendered statement PDF (clean format) directly, via a hidden iframe,
  // so the printout matches the downloaded PDF. Falls back to opening it if a browser blocks
  // programmatic print of an embedded PDF.
  const printStatement = () => {
    let frame = document.getElementById('statement-pdf-print-frame')
    if (!frame) {
      frame = document.createElement('iframe')
      frame.id = 'statement-pdf-print-frame'
      frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
      document.body.appendChild(frame)
    }
    frame.onload = () => {
      try { frame.contentWindow.focus(); frame.contentWindow.print() }
      catch { window.open(statementUrl, '_blank', 'noopener') }
    }
    frame.src = statementUrl
  }

  const askMethod = () => (window.prompt('Method: card / cash / eft', 'card') || '').toLowerCase()
  const takeRefund = () => {
    const amount = window.prompt(`Refund from credit (available R${Number(balance.credit || 0).toFixed(2)}):`)
    if (!amount) return
    const method = askMethod(); if (!method) return
    const reason = window.prompt('Reason (optional):') || ''
    router.post(`/accounts/${account.id}/refund`, { amount, method, reason }, { preserveScroll: true })
  }

  const members = account.members || []
  // Who a statement share goes to — the selected member (if scoped), else the account head. Mirrors
  // BillingAccountsController#statement_recipient on the server (Paul 2026-08-04).
  const statementRecipient = scope === 'all' ? (members.find((m) => m.is_head) || members[0]) : members.find((m) => String(m.id) === String(scope))
  const [sendingWa, setSendingWa] = useState(false)
  const sendStatementWhatsapp = () => {
    if (!window.confirm(`Send the account statement to ${statementRecipient?.name || 'the patient'} on WhatsApp, with the PDF attached?`)) return
    setSendingWa(true)
    // Send EXACTLY what is on the screen — the chosen range and the chosen member. Both were
    // dropped here, so the patient received a default 2-year, whole-family statement no matter
    // what reception had selected (Paul 2026-08-12).
    router.post(`/accounts/${account.id}/send_whatsapp`, statementScope(), {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(`Sent to ${statementRecipient?.name || 'the patient'} on WhatsApp`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not send'),
      onFinish:  () => setSendingWa(false),
    })
  }
  const [sendingEmail, setSendingEmail] = useState(false)
  const sendStatementEmail = () => {
    if (!window.confirm(`Email the account statement to ${statementRecipient?.name || 'the patient'}?`)) return
    setSendingEmail(true)
    router.post(`/accounts/${account.id}/send_email`, statementScope(), {
      preserveScroll: true,
      onSuccess: (page) => { const al = page?.props?.flash?.alert; al ? toast.error(al) : toast.success(`Emailed to ${statementRecipient?.name || 'the patient'}`) },
      onError:   (errs) => toast.error(Object.values(errs || {})[0] || 'Could not email'),
      onFinish:  () => setSendingEmail(false),
    })
  }
  const memberConvos = (pid) => conversations.filter((c) => String(c.patient_id) === String(pid))
  const setHead = (m) => {
    if (m.is_head) return
    if (!window.confirm(`Make ${m.name} the head of this account?\n\n• The statement is addressed to them and deposits/refunds are attributed to them.\n• Everyone else on the account becomes a dependant.`)) return
    router.post(`/accounts/${account.id}/set_head`, { patient_id: m.id }, { preserveScroll: true })
  }

  // Whose line is this? Shown on the whole-family ledger only — the way Elixir put the dependant
  // beside every charge, so a four-person account never blurs into one column of numbers.
  const memberName = (pid) => {
    const m = members.find((x) => String(x.id) === String(pid))
    return m ? m.name.split(' ')[0] : null
  }

  // Running balance recomputed for the active scope (whole account, or one member).
  const view = useMemo(() => {
    const rows = scope === 'all' ? transactions : transactions.filter((t) => String(t.patient_id) === String(scope))
    const opening = scope === 'all' ? Number(opening_balance || 0) : Number(openings_by_patient?.[scope] || 0)
    let bal = opening
    const withBal = rows.map((t) => { bal += (Number(t.debit) || 0) - (Number(t.credit) || 0); return { ...t, balance: bal } })
    return { opening, rows: withBal, closing: bal }
  }, [scope, transactions, opening_balance, openings_by_patient])

  const scopedInvoices = scope === 'all' ? invoices : invoices.filter((i) => String(i.patient_id) === String(scope))
  const scopedConvos = scope === 'all' ? conversations : memberConvos(scope)

  return (
    <DashboardLayout>
      <Link href="/accounts" className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> Back to accounts
      </Link>

      <div className="mb-5 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Wallet size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">{account.billing_name}</h1>
          <p className="font-mono text-sm text-brand-muted">
            {account.account_code}
            {(() => { const h = members.find((m) => m.is_head); return h ? <span className="ml-2 font-sans text-brand-muted">· ★ Head: <span className="font-medium text-brand-ink">{h.name}</span></span> : <span className="ml-2 font-sans text-amber-700">· no head set — tick one below</span> })()}
          </p>
        </div>
      </div>

      {/* Balance summary */}
      <div className="mb-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total billed" value={rand(balance.billed)} />
        <Stat label="Total paid" value={rand(balance.paid)} />
        <Stat label="Outstanding" value={rand(balance.outstanding)} accent={(balance.outstanding || 0) > 0 ? 'text-brand-danger' : 'text-emerald-600'} />
        <Stat label="Credit available" value={rand(balance.credit)} accent={(balance.credit || 0) > 0 ? 'text-indigo-600' : 'text-brand-muted'} />
      </div>

      {/* Risk + ageing + credit-source insight row */}
      <div className="mb-4 grid grid-cols-1 gap-3 lg:grid-cols-3">
        <RiskBadge risk={risk} />
        <AgeingBox ageing={ageing} />
        <CreditBox credit_breakdown={credit_breakdown} />
      </div>

      {/* Account-level money actions */}
      <div className="mb-5 flex flex-wrap gap-2">
        <button onClick={() => setPayOpen(true)} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-700">
          <Plus size={14} /> Add payment
        </button>
        {(balance.credit || 0) > 0 && (
          <button onClick={takeRefund} className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-1.5 text-sm text-brand-muted hover:bg-brand-surface">
            Refund credit
          </button>
        )}
      </div>

      {/* Date range + statement */}
      <div className="mb-4 flex flex-wrap items-end gap-3 rounded-xl border border-brand-border bg-white p-4">
        <div>
          <label className="block text-xs font-medium text-brand-muted">From</label>
          <div className="mt-1"><DatePopover value={from} onChange={setFrom} /></div>
        </div>
        <div>
          <label className="block text-xs font-medium text-brand-muted">To</label>
          <div className="mt-1"><DatePopover value={to} onChange={setTo} /></div>
        </div>
        <button onClick={applyRange} className="rounded-lg border border-brand-border px-3 py-2 text-sm font-medium text-brand-ink hover:bg-brand-surface">View transactions</button>
        {members.length > 1 && (
          <div>
            <label className="block text-xs font-medium text-brand-muted">Statement for</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
              title="Print the whole account, or just this member's transactions"
              className="mt-1 rounded-lg border border-brand-border bg-white px-3 py-2 text-sm text-brand-ink">
              <option value="all">All members</option>
              {members.map((m) => <option key={m.id} value={String(m.id)}>{m.name} only</option>)}
            </select>
          </div>
        )}
        <button onClick={printStatement} title={scope === 'all' ? 'Print the statement for the whole account' : 'Print the statement for the selected member only'} className="inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:brightness-95">
          <Printer size={14} /> Print statement
        </button>
        <a href={statementUrl} target="_blank" rel="noreferrer" title="Open / download the statement PDF" className="inline-flex items-center gap-1.5 rounded-lg border border-brand-border px-3 py-2 text-sm font-medium text-brand-ink hover:bg-brand-surface">
          <FileText size={14} /> PDF
        </a>
        <button
          onClick={sendStatementWhatsapp}
          disabled={sendingWa || !statementRecipient?.phone}
          className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-700 shadow-sm hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={statementRecipient?.phone ? `Send the statement to ${statementRecipient.name} on WhatsApp (PDF attached)` : 'No mobile number on file for this patient — add one on their profile'}
        >
          <MessageCircle size={14} /> {sendingWa ? 'Sending…' : 'Send on WhatsApp'}
        </button>
        <button
          onClick={sendStatementEmail}
          disabled={sendingEmail || !statementRecipient}
          className="inline-flex items-center gap-1.5 rounded-lg border border-brand-primary bg-white px-3 py-2 text-sm font-semibold text-brand-primary shadow-sm hover:bg-brand-primary hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
          title={statementRecipient ? `Email the statement to ${statementRecipient.name}` : 'No head of account set for this patient — set one below'}
        >
          <Mail size={14} /> {sendingEmail ? 'Sending…' : 'Send email'}
        </button>
      </div>

      {/* Tabs */}
      <div className="mb-3 flex items-center gap-1 border-b border-brand-border">
        {[
          { k: 'ledger', label: 'Ledger', icon: ScrollText },
          { k: 'comms', label: `Communication${conversations.length ? ` (${conversations.length})` : ''}`, icon: MessageSquare },
        ].map((t) => {
          const Icon = t.icon
          const active = tab === t.k
          return (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium transition-colors ${active ? 'border-brand-primary text-brand-primary' : 'border-transparent text-brand-muted hover:text-brand-ink'}`}>
              <Icon size={14} /> {t.label}
            </button>
          )
        })}
      </div>

      {/* Scope selector (applies to Ledger + Communication) */}
      {members.length > 1 && (
        <div className="mb-3 flex flex-wrap gap-1.5">
          <button onClick={() => setScope('all')}
            className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${scope === 'all' ? 'bg-brand-primary text-white' : 'border border-brand-border bg-white text-brand-muted hover:bg-brand-surface'}`}>
            All members
          </button>
          {members.map((m) => (
            <button key={m.id} onClick={() => setScope(String(m.id))}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${String(scope) === String(m.id) ? 'bg-brand-primary text-white' : 'border border-brand-border bg-white text-brand-muted hover:bg-brand-surface'}`}>
              {m.name}
            </button>
          ))}
        </div>
      )}

      {tab === 'ledger' && (
        <>
          {/* Transactions ledger (scoped) */}
          <div className="mb-5 overflow-hidden rounded-xl border border-brand-border bg-white">
            <div className="border-b border-brand-border bg-brand-surface px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-muted">
              {scope === 'all' ? 'Account ledger' : `${members.find((m) => String(m.id) === String(scope))?.name || 'Patient'} ledger`} · {dz(period.from)} – {dz(period.to)}
            </div>
            {/* Elixir-style ledger (Paul 2026-08-12): one row per CHARGED CODE, with the payment
                sitting directly under the charges it settled. Reception reads it straight off the
                screen when a patient asks "what am I actually paying for?" — the old one-line-per-
                invoice view could only answer "treatment". Scrolls inside itself on a small screen
                so the page body never scrolls sideways. */}
            <div className="overflow-x-auto">
            <table className="w-full min-w-[860px] text-sm">
              <thead className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
                <tr>
                  <th className="px-3 py-2 font-semibold">Date</th>
                  <th className="px-3 py-2 font-semibold">Code</th>
                  <th className="px-2 py-2 text-center font-semibold">Units</th>
                  {scope === 'all' && <th className="px-3 py-2 font-semibold">Member</th>}
                  <th className="px-2 py-2 text-center font-semibold">Tooth</th>
                  <th className="px-3 py-2 font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="px-3 py-2 text-right font-semibold"
                      title="This line's share of what has been paid on the invoice, allocated by line value. A payment is recorded against the invoice, not the line.">
                    Paid<span className="ml-0.5 font-normal text-brand-muted">*</span>
                  </th>
                  <th className="px-3 py-2 text-right font-semibold">Credit</th>
                  <th className="px-3 py-2 text-right font-semibold">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-brand-border/60 italic text-brand-muted">
                  <td className="px-3 py-2">{dz(period.from)}</td>
                  <td colSpan={scope === 'all' ? 6 : 5} className="px-3 py-2">Opening balance</td>
                  <td />
                  <td className="px-3 py-2 text-right">{rand(view.opening)}</td>
                </tr>
                {view.rows.map((t, i) => {
                  const isPayment = t.credit > 0
                  const first = !t.line_index                       // first line of an invoice
                  const last = t.line_count ? t.line_index === t.line_count - 1 : true
                  return (
                    <tr
                      key={i}
                      className={cn(
                        isPayment
                          ? 'bg-emerald-50/70 font-medium'          // payments read as a block, like Elixir's blue rows
                          : last ? 'border-b border-brand-border/40' : '',
                        isPayment && 'border-b border-emerald-200',
                      )}
                    >
                      <td className="whitespace-nowrap px-3 py-1.5 text-brand-ink">{first || isPayment ? dz(t.date) : ''}</td>
                      <td className="px-3 py-1.5 font-mono text-xs font-semibold text-brand-ink">{t.code || (isPayment ? t.ref : '')}</td>
                      <td className="px-2 py-1.5 text-center text-xs text-brand-muted">{t.units > 1 ? t.units : ''}</td>
                      {scope === 'all' && (
                        <td className="whitespace-nowrap px-3 py-1.5 text-xs text-brand-muted">
                          {first || isPayment ? (memberName(t.patient_id) || '—') : ''}
                        </td>
                      )}
                      <td className="px-2 py-1.5 text-center font-mono text-xs text-brand-ink">{t.tooth || ''}</td>
                      <td className={cn('px-3 py-1.5', isPayment ? 'text-emerald-800' : 'text-brand-ink')}>
                        {t.description}
                        {isPayment && <TagChip tag={t.tag} />}
                        {first && !isPayment && t.provider && (
                          <span className="ml-2 font-mono text-[11px] text-brand-muted">{t.ref} · {t.provider}</span>
                        )}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-brand-ink">{t.debit > 0 ? rand(t.debit) : ''}</td>
                      {/* This line's share of what has been paid — allocated by line value, and
                          marked as derived. The real payment row still appears in full below. */}
                      <td className={cn('whitespace-nowrap px-3 py-1.5 text-right',
                        isPayment ? '' : t.allocated > 0 ? 'text-emerald-700' : 'text-brand-muted')}>
                        {isPayment ? '' : (t.allocated > 0 ? rand(t.allocated) : (t.debit > 0 ? '—' : ''))}
                      </td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-emerald-700">{t.credit > 0 ? rand(t.credit) : ''}</td>
                      <td className="whitespace-nowrap px-3 py-1.5 text-right text-brand-ink">{last || isPayment ? rand(t.balance) : ''}</td>
                    </tr>
                  )
                })}
                <tr className="bg-brand-surface/60 font-semibold">
                  <td className="px-3 py-2">{dz(period.to)}</td>
                  <td colSpan={scope === 'all' ? 6 : 5} className="px-3 py-2">Closing balance</td>
                  <td />
                  <td className="px-3 py-2 text-right">{rand(view.closing)}</td>
                </tr>
                {view.rows.length === 0 && (
                  <tr><td colSpan={scope === 'all' ? 10 : 9} className="px-4 py-6 text-center text-brand-muted">No transactions in this period.</td></tr>
                )}
              </tbody>
            </table>
            </div>
          </div>

          {/* Invoices (each is a tax invoice for its date) */}
          {scopedInvoices.length > 0 && (
            <div className="mb-5 overflow-hidden rounded-xl border border-brand-border bg-white">
              <div className="border-b border-brand-border bg-brand-surface px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-brand-muted">
                Tax invoices
              </div>
              <table className="w-full text-sm">
                <tbody>
                  {scopedInvoices.map((inv) => (
                    <tr key={inv.id} className="border-b border-brand-border/40 last:border-0">
                      <td className="px-4 py-2 text-brand-muted">{dz(inv.date)}</td>
                      <td className="px-4 py-2"><Link href={`/invoices/${inv.id}`} className="inline-flex items-center gap-1.5 font-mono text-brand-primary hover:underline"><FileText size={13} /> {inv.number}</Link></td>
                      <td className="px-4 py-2 text-xs text-brand-muted">{inv.provider}</td>
                      <td className="px-4 py-2 text-right text-brand-ink">{rand(inv.total)}</td>
                      <td className="px-4 py-2 text-right text-xs"><span className="rounded-md bg-brand-surface px-2 py-0.5 text-brand-muted">{inv.status}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {tab === 'comms' && (
        <div className="mb-5 space-y-2">
          {scopedConvos.length === 0 ? (
            <div className="rounded-xl border border-brand-border bg-white px-4 py-8 text-center text-sm text-brand-muted">
              No WhatsApp / voice conversations recorded {scope === 'all' ? 'for this account' : 'for this patient'} yet.
            </div>
          ) : (
            scopedConvos.map((c) => <ConversationCard key={c.id} convo={c} />)
          )}
        </div>
      )}

      {/* Members */}
      <div className="overflow-hidden rounded-xl border border-brand-border bg-white">
        <div className="flex items-center justify-between border-b border-brand-border bg-brand-surface px-4 py-2.5">
          <span className="text-xs font-semibold uppercase tracking-wide text-brand-muted">Members on this account</span>
          <span className="text-[11px] text-brand-muted">Tick the <strong className="text-amber-700">★ head</strong> — everyone else becomes a dependant</span>
        </div>
        <table className="w-full text-sm">
          <thead className="border-b border-brand-border text-left text-xs uppercase tracking-wide text-brand-muted">
            <tr>
              <th className="px-4 py-2 text-center font-semibold">Head</th>
              <th className="px-4 py-2 font-semibold">Member</th>
              <th className="px-4 py-2 font-semibold">Role</th>
              <th className="px-4 py-2 font-semibold">Next appointment</th>
              <th className="px-4 py-2 text-right font-semibold">Owes</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {members.map((m) => (
              <tr key={m.id} className={`border-b border-brand-border/60 last:border-0 ${m.is_head ? 'bg-amber-50/40' : ''}`}>
                <td className="px-4 py-2.5 text-center">
                  <input
                    type="radio"
                    name="account_head"
                    checked={!!m.is_head}
                    onChange={() => setHead(m)}
                    title={m.is_head ? 'This member is the head of the account' : `Make ${m.name} the head of the account`}
                    className="h-4 w-4 cursor-pointer accent-amber-600"
                  />
                </td>
                <td className="px-4 py-2.5">
                  <Link href={`/patients/${m.id}`} className="text-brand-ink hover:underline">{m.name}</Link>
                </td>
                <td className="px-4 py-2.5">
                  {m.is_head
                    ? <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">★ Head of account</span>
                    : <span className="text-xs text-brand-muted">Dependant</span>}
                </td>
                <td className="px-4 py-2.5 text-brand-muted">
                  {m.next_appointment
                    ? new Date(m.next_appointment).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
                    : <span className="text-brand-muted/60">— none booked —</span>}
                </td>
                <td className="px-4 py-2.5 text-right">{(m.outstanding || 0) > 0 ? <span className="font-medium text-brand-danger">{rand(m.outstanding)}</span> : <span className="text-brand-muted">—</span>}</td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <Link href={`/patients/${m.id}`} className="text-xs font-medium text-brand-primary hover:underline">Book / view →</Link>
                </td>
              </tr>
            ))}
            {members.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-brand-muted">No members linked yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <PaymentModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        account={{ id: account.id, outstanding: balance.outstanding }}
        onSaved={() => { try { (account.members || []).forEach((m) => { if (m?.id) localStorage.setItem('patient_stale:' + m.id, '1') }) } catch (e) {} }}
      />
    </DashboardLayout>
  )
}
